import React from 'react';

interface PDTRecordFormattedViewProps {
  data: any;
}

const PDTRecordFormattedView: React.FC<PDTRecordFormattedViewProps> = ({ data }) => {
  const position = data.position || {};
  const designator = data.designator || {};
  const result = data.determinationResult || {};
  const nationalSecurityDuties = data.nationalSecurityDuties || [];
  const suitabilityDuties = data.suitabilityDuties || [];
  const metadata = data.metadata || {};

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      {/* Header */}
      <div className="mb-6 text-right text-sm text-gray-600">
        {data.requestId || 'N/A'}
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-8">Position Designation Record</h1>

      {/* Position Information */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-8 text-sm">
        <div>
          <span className="font-semibold">Department</span>
          <div className="text-gray-700">{position.department || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Agency</span>
          <div className="text-gray-700">{position.agency || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Supplemental Duty</span>
          <div className="text-gray-700">{position.supplementalDuty || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Position Title</span>
          <div className="text-gray-700">{position.title || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Position Description</span>
          <div className="text-gray-700">{position.description || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Series and Grade/Pay Band</span>
          <div className="text-gray-700">{position.seriesPayBand || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Position Description Number</span>
          <div className="text-gray-700">{position.descriptionNumber || 'N/A'}</div>
        </div>
        <div>
          <span className="font-semibold">Designator's Name & Title</span>
          <div className="text-gray-700">{designator.name || 'N/A'}</div>
        </div>
      </div>

      {/* Final Position Designation and Investigation */}
      <h2 className="text-xl font-bold text-gray-900 mb-4">Final Position Designation and Investigation</h2>

      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-300 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Sensitivity Level</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Risk Level</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Level of Investigation per Federal Investigative Standards (2012)</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Level of Investigation per 2022 Federal Personnel Vetting Investigative Standards</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">SF Form</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">PVQ Parts</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 px-4 py-2">{result.sensitivityDesignation || 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2">{result.riskDesignation || 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2">{result.investigation || 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2">{result.investigationTier || 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2">{result.formType || 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2">{result.pvqParts || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Points Table */}
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-300 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Label</th>
              <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Points</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 px-4 py-2">Total Initial Position Designation Points from Step 2</td>
              <td className="border border-gray-300 px-4 py-2">{result.initialRiskPoints || 0}</td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-4 py-2">Adjusted Position Designation Points from Step 3</td>
              <td className="border border-gray-300 px-4 py-2">{result.adjustmentPoints || 0}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <h2 className="text-xl font-bold text-gray-900 mb-4">Summary</h2>

      {/* National Security */}
      {nationalSecurityDuties && nationalSecurityDuties.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">National Security</h3>
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full border border-gray-300 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">National Duties</th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Degree of Potential for Compromise or Damage</th>
                </tr>
              </thead>
              <tbody>
                {nationalSecurityDuties.map((duty: any, idx: number) => (
                  <tr key={idx}>
                    <td className="border border-gray-300 px-4 py-2 align-top">
                      {duty.categoryShortDesc || duty.categoryLongDesc || 'N/A'}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="font-semibold mb-1">{duty.impactTitle || duty.impactLevel}</div>
                      <div className="text-gray-700 whitespace-pre-line">{duty.impactDescription || 'N/A'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Suitability */}
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Suitability</h3>
      {suitabilityDuties && suitabilityDuties.length > 0 ? (
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full border border-gray-300 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Public Trust Duties</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {suitabilityDuties.map((duty: any, idx: number) => (
                <tr key={idx}>
                  <td className="border border-gray-300 px-4 py-2">{duty.categoryShortDesc || duty.categoryLongDesc || 'N/A'}</td>
                  <td className="border border-gray-300 px-4 py-2">{duty.impactDescription || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mb-8 text-gray-600">No Public Trust duties selected</div>
      )}

      {/* Other Duties */}
      {data.nationalSecurityDutiesOther && (
        <div className="mb-4">
          <span className="font-semibold">National Security Duties (Other): </span>
          <span className="text-gray-700">{data.nationalSecurityDutiesOther}</span>
        </div>
      )}
      {data.suitabilityDutiesOther && (
        <div className="mb-4">
          <span className="font-semibold">Suitability Duties (Other): </span>
          <span className="text-gray-700">{data.suitabilityDutiesOther}</span>
        </div>
      )}

      {/* Signature Section */}
      <div className="mt-8 pt-6 border-t border-gray-300">
        <div className="flex justify-between items-end">
          <div className="flex-1">
            <div className="mb-2">
              <span className="font-semibold">Designator's Name: </span>
              <span>{designator.name || 'N/A'}</span>
            </div>
            <div>
              <span className="font-semibold">Designator's Signature: </span>
              <span className="inline-block border-b border-gray-400 w-64 ml-2"></span>
            </div>
          </div>
          <div>
            <span className="font-semibold">Date: </span>
            <span className="inline-block border-b border-gray-400 w-32 ml-2"></span>
          </div>
        </div>
      </div>

      {/* Metadata (if present) */}
      {metadata && Object.keys(metadata).length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-300">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Metadata</h3>
          <div className="bg-gray-50 rounded p-4 text-sm">
            <pre className="whitespace-pre-wrap text-xs text-gray-700">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDTRecordFormattedView;
